import { formatDate } from '@resclick/crs-ui';
import { compass } from '@resclick/compass';
import { ICart, IProductProperty, PROPERTY_TYPES } from '@resclick/data-access';
import { date, Notify } from 'quasar';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import subtractFromDate = date.subtractFromDate;
import { i18n } from '@/i18n';

const createCustomer = (): ICart['customer'] => ({
  name: '',
  surname: '',
  gender: 'MALE',
  email: '',
  phoneNumber: {
    countryCode: '',
    areaCode: '',
    number: '',
  },
  passportNumber: '',
  address: {
    line1: '',
    line2: '',
  },
});

const createInvoice = (): ICart['invoice'] => ({
  type: 'INDIVIDUAL',
  title: '',
  taxNumber: '',
  taxAdministration: '',
  address: {
    line1: '',
    line2: '',
  },
});

const createCartTotal = () => ({
  value: 0,
  currency: '',
});

@Module({ namespaced: true })
export default class CartStore extends VuexModule implements ICart {
  products: ICart['products'] = [];
  isLoading = true;
  total = createCartTotal();
  customer = createCustomer();
  invoice = createInvoice();
  company = '';

  constructor(args: any) {
    super(args);
    this.isLoading = false;
  }

  @Action({ commit: 'SET_CART' })
  public async fetchCart() {
    this.context.commit('IS_LOADING', true);
    const { data } = await compass.do('carts', 'findOne', {
      query: {
        currency: this.context.rootState?.user?.preferences?.currency,
      },
      populate: [
        {
          path: 'products.room',
          select: ['displayName', 'property'],
          populate: [
            {
              path: 'property',
              select: ['name', 'company'],
            },
          ],
        },
        {
          path: 'products.ratePlan',
          select: ['name'],
        },
        {
          path: 'products.concept',
          select: ['name'],
        },
        {
          path: 'products.cancellationPolicy',
          select: ['name'],
        },
      ],
    });

    this.context.commit('IS_LOADING', false);
    return data;
  }

  @Action
  public async addToCart(product: any) {
    this.context.commit('IS_LOADING', true);

    if (PROPERTY_TYPES.includes(product.type)) {
      product.checkInDate = new Date(product.checkInDate);
      product.checkOutDate = new Date(product.checkOutDate);
      product.bookingDate = new Date(product.bookingDate);

      if (product.adults) {
        product.adults = Array.from({ length: product.adults }, () => ({
          gender: 'MALE',
          name: '',
          surname: '',
        }));
      }

      if (product.children) {
        product.children = product.children.map((age: number) => {
          return {
            gender: 'MALE',
            name: '',
            surname: '',
            dateOfBirth: formatDate(subtractFromDate(new Date(), { year: age }), 'YYYY-MM-DD'),
          };
        });
      }
    }

    compass
      .do('carts', 'addProduct', {
        data: product,
      })
      .then(() => this.context.dispatch('fetchCart'))
      .catch((err) => {
        if (err.status === 403) {
          Notify.create({
            message: i18n.t('MESSAGE.NO_ENOUGH_INVENTORY') as string,
            type: 'negative',
          });
        }
      })
      .finally(() => this.context.commit('IS_LOADING', false));
  }

  @Action
  public removeFromCart(index: number) {
    this.context.commit('IS_LOADING', true);
    const product = this.products[index];
    compass
      .do('carts', 'removeProduct', {
        params: { productId: product._id as string },
      })
      .then(() => this.context.dispatch('fetchCart'))
      .finally(() => this.context.commit('IS_LOADING', false));
  }

  @Action
  public clearCart() {
    this.context.commit('IS_LOADING', true);
    compass
      .do('carts', 'clearCart', {})
      .then((res) => this.context.commit('SET_CART', res.data))
      .finally(() => this.context.commit('IS_LOADING', false));
  }

  @Action
  public saveCart(cart: ICart) {
    cart.products = cart.products.map((product) => {
      if (PROPERTY_TYPES.includes(product.type as any)) {
        product = product as IProductProperty;
        product.room = (product.room as any).id || product.room;
      }
      return product;
    });

    compass
      .do('carts', 'update', {
        data: cart as any,
      })
      .then(() => this.context.dispatch('fetchCart'))
      .finally(() => this.context.commit('IS_LOADING', false));
  }

  @Mutation
  public IS_LOADING(value: boolean) {
    this.isLoading = value;
  }

  @Mutation
  SET_CART(cart: ICart) {
    this.products = cart.products;
    this.invoice = Object.assign({}, createInvoice(), cart.invoice);
    this.customer = Object.assign({}, createCustomer(), cart.customer);
    this.total = cart.total ? cart.total : createCartTotal();

    // @ts-ignore
    this.company = cart.company;
  }
}
